import state, { createStroke } from "./state";
import "./styles.scss";
import { ChevronUp, CircleHelp, createIcons, Download, Hand, Palette, Trash2, Undo2, X, } from "lucide";
import throttle from "./functions/throttle";
import drawAlignmentDots from "./functions/drawAlignmentDots";
import undoLastStroke from "./functions/undoLastStroke";
import saveImage from "./functions/saveImage";
import useColorPicker, { applyColor } from "./functions/useColorPicker";
import clearBoard from "./functions/clearBoard";
import renderBoard, { renderPanPreview } from "./functions/renderBoard";
import { screenToWorld, zoomAtPoint } from "./functions/viewport";
document.addEventListener("DOMContentLoaded", () => {
    const mainCanvas = document.getElementById("drawingCanvas");
    const alignmentCanvas = document.getElementById("alignmentCanvas");
    if (!mainCanvas || !alignmentCanvas) {
        console.error("Canvas element not found");
        return;
    }
    const mainCtx = mainCanvas.getContext("2d", { willReadFrequently: true });
    const alignmentCtx = alignmentCanvas.getContext("2d");
    if (!mainCtx || !alignmentCtx) {
        console.error("Failed to get 2D context");
        return;
    }
    const saveBtn = document.getElementById("saveBtn");
    const clearBtn = document.getElementById("clearBtn");
    const undoBtn = document.getElementById("undoBtn");
    const colorBtn = document.getElementById("colorBtn");
    const colorDropdown = document.getElementById("colorDropdown");
    const currentColor = document.querySelector(".current-color");
    const colorValue = document.getElementById("colorValue");
    const customColor = document.getElementById("customColor");
    const clearDialog = document.getElementById("clearDialog");
    const colorOptions = document.querySelectorAll(".color-option");
    const strokeSizeBtn = document.getElementById("strokeSizeBtn");
    const strokeSizeDropdown = document.getElementById("strokeSizeDropdown");
    const strokeSizeValue = document.getElementById("strokeSizeValue");
    const currentStroke = document.querySelector(".current-stroke");
    const strokeSizeOptions = document.querySelectorAll(".stroke-size-option");
    const helpBtn = document.getElementById("helpBtn");
    const helpDialog = document.getElementById("helpDialog");
    if (!saveBtn ||
        !clearBtn ||
        !undoBtn ||
        !colorBtn ||
        !colorDropdown ||
        !currentColor ||
        !colorValue ||
        !customColor ||
        !clearDialog ||
        !strokeSizeBtn ||
        !strokeSizeDropdown ||
        !strokeSizeValue ||
        !currentStroke ||
        !helpBtn ||
        !helpDialog) {
        console.error("Board controls not found");
        return;
    }
    createIcons({
        icons: {
            ChevronUp,
            CircleHelp,
            Download,
            Hand,
            Palette,
            Trash2,
            Undo2,
            X,
        },
    });
    mainCanvas.width = window.innerWidth;
    mainCanvas.height = window.innerHeight;
    alignmentCanvas.width = window.innerWidth;
    alignmentCanvas.height = window.innerHeight;
    state.init();
    let selectedStrokeSize = 3;
    const configureDrawingContext = (color) => {
        mainCtx.strokeStyle = color;
        mainCtx.lineWidth = selectedStrokeSize;
        mainCtx.lineCap = "round";
        mainCtx.shadowColor = color;
        mainCtx.shadowBlur = 1;
        mainCtx.imageSmoothingEnabled = true;
        mainCtx.imageSmoothingQuality = "high";
    };
    configureDrawingContext("#ffffff");
    const closeColorPopover = () => {
        colorDropdown.hidden = true;
        colorBtn.setAttribute("aria-expanded", "false");
    };
    const closeStrokeSizePopover = () => {
        strokeSizeDropdown.hidden = true;
        strokeSizeBtn.setAttribute("aria-expanded", "false");
    };
    const selectColor = (color) => {
        const normalizedColor = color.toLowerCase();
        applyColor(mainCtx, normalizedColor);
        currentColor.style.backgroundColor = normalizedColor;
        colorValue.textContent = normalizedColor.toUpperCase();
        if (/^#[0-9a-f]{6}$/.test(normalizedColor)) {
            customColor.value = normalizedColor;
        }
        colorOptions.forEach((option) => {
            var _a;
            option.setAttribute("aria-pressed", String(((_a = option.dataset.color) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === normalizedColor));
        });
    };
    const selectStrokeSize = (size) => {
        selectedStrokeSize = size;
        mainCtx.lineWidth = size;
        currentStroke.style.setProperty("--stroke-size", `${size}px`);
        strokeSizeValue.textContent = `${size} px`;
        strokeSizeOptions.forEach((option) => {
            option.setAttribute("aria-pressed", String(Number(option.dataset.size) === size));
        });
    };
    const refreshControls = () => {
        const boardIsEmpty = state.getStrokes().length === 0;
        undoBtn.disabled = boardIsEmpty;
        clearBtn.disabled = boardIsEmpty;
        saveBtn.disabled = boardIsEmpty;
    };
    selectColor("#ffffff");
    selectStrokeSize(selectedStrokeSize);
    refreshControls();
    const render = () => {
        renderBoard(mainCanvas, mainCtx, state.getStrokes(), state.getCamera(), state.getActiveStroke());
        drawAlignmentDots(alignmentCanvas, alignmentCtx, state.getCamera());
    };
    const getCanvasPoint = (event) => {
        const bounds = mainCanvas.getBoundingClientRect();
        return {
            x: event.clientX - bounds.left,
            y: event.clientY - bounds.top,
        };
    };
    render();
    const startDrawing = (event) => {
        state.setDrawing(true);
        const point = screenToWorld(getCanvasPoint(event), state.getCamera());
        state.setActiveStroke(createStroke(point, String(mainCtx.strokeStyle), mainCtx.lineWidth));
    };
    const draw = (event) => {
        if (!state.getDrawing())
            return;
        const activeStroke = state.getActiveStroke();
        if (!activeStroke)
            return;
        activeStroke.points.push(screenToWorld(getCanvasPoint(event), state.getCamera()));
        render();
    };
    const throttledDraw = throttle(draw, 16);
    const stopDrawing = () => {
        if (!state.getDrawing())
            return;
        state.setDrawing(false);
        const activeStroke = state.getActiveStroke();
        if (activeStroke && activeStroke.points.length >= 4) {
            state.setStrokes([...state.getStrokes(), activeStroke]);
        }
        state.setActiveStroke(null);
        render();
        refreshControls();
    };
    let panStart = null;
    let panSnapshot = null;
    const startPanning = (event) => {
        stopDrawing();
        const camera = state.getCamera();
        panSnapshot = document.createElement("canvas");
        panSnapshot.width = mainCanvas.width;
        panSnapshot.height = mainCanvas.height;
        const snapshotCtx = panSnapshot.getContext("2d");
        if (snapshotCtx) {
            snapshotCtx.drawImage(mainCanvas, 0, 0);
        }
        else {
            panSnapshot = null;
        }
        state.setPanning(true);
        panStart = {
            clientX: event.clientX,
            clientY: event.clientY,
            panX: camera.panX,
            panY: camera.panY,
        };
        mainCanvas.classList.add("panning");
    };
    const pan = (event) => {
        if (!state.getPanning() || !panStart)
            return;
        state.setCamera(Object.assign(Object.assign({}, state.getCamera()), { panX: panStart.panX + event.clientX - panStart.clientX, panY: panStart.panY + event.clientY - panStart.clientY }));
        const camera = state.getCamera();
        if (panSnapshot) {
            renderPanPreview(mainCanvas, mainCtx, panSnapshot, camera.panX - panStart.panX, camera.panY - panStart.panY);
            drawAlignmentDots(alignmentCanvas, alignmentCtx, camera);
        }
        else {
            render();
        }
    };
    const stopPanning = () => {
        if (!state.getPanning())
            return;
        state.setPanning(false);
        panStart = null;
        panSnapshot = null;
        mainCanvas.classList.remove("panning");
        render();
    };
    // Handle mouse events
    mainCanvas.addEventListener("mousedown", (e) => {
        if (e.button === 2) {
            e.preventDefault();
            startPanning(e);
        }
        else if (e.button === 1) {
            e.preventDefault();
            closeColorPopover();
            closeStrokeSizePopover();
            useColorPicker(e, selectColor);
        }
        else if (e.button === 0) {
            startDrawing(e);
        }
    });
    mainCanvas.addEventListener("mousemove", (e) => {
        if (state.getPanning()) {
            pan(e);
        }
        else if (state.getDrawing()) {
            throttledDraw(e);
        }
    });
    window.addEventListener("mouseup", (e) => {
        if (e.button === 2) {
            stopPanning();
        }
        else if (e.button === 0) {
            stopDrawing();
        }
    });
    mainCanvas.addEventListener("wheel", (e) => {
        e.preventDefault();
        if (state.getPanning())
            return;
        stopDrawing();
        const zoomFactor = Math.exp(-e.deltaY * 0.001);
        state.setCamera(zoomAtPoint(state.getCamera(), getCanvasPoint(e), zoomFactor));
        render();
    }, { passive: false });
    let touchNavigating = false;
    let touchGesture = null;
    const getTouchPair = (touches) => [
        touches[0],
        touches[1],
    ];
    const getTouchGeometry = (first, second) => {
        const firstPoint = getCanvasPoint(first);
        const secondPoint = getCanvasPoint(second);
        return {
            midpoint: {
                x: (firstPoint.x + secondPoint.x) / 2,
                y: (firstPoint.y + secondPoint.y) / 2,
            },
            distance: Math.hypot(secondPoint.x - firstPoint.x, secondPoint.y - firstPoint.y),
        };
    };
    const beginTouchGesture = (touches) => {
        const [first, second] = getTouchPair(touches);
        touchGesture = Object.assign(Object.assign({ identifiers: [first.identifier, second.identifier] }, getTouchGeometry(first, second)), { camera: state.getCamera() });
    };
    // Handle touch events
    mainCanvas.addEventListener("touchstart", handleTouchStart, {
        passive: false,
    });
    mainCanvas.addEventListener("touchmove", handleTouchMove, { passive: false });
    mainCanvas.addEventListener("touchend", handleTouchEnd, { passive: false });
    mainCanvas.addEventListener("touchcancel", handleTouchEnd, {
        passive: false,
    });
    function handleTouchStart(e) {
        e.preventDefault();
        if (e.touches.length >= 2) {
            if (!touchNavigating)
                stopDrawing();
            touchNavigating = true;
            beginTouchGesture(e.touches);
            return;
        }
        if (touchNavigating || e.touches.length !== 1)
            return;
        const touch = e.touches[0];
        const mouseEvent = new MouseEvent("mousedown", {
            clientX: touch.clientX,
            clientY: touch.clientY,
        });
        mainCanvas.dispatchEvent(mouseEvent);
    }
    function handleTouchMove(e) {
        e.preventDefault();
        if (touchNavigating) {
            if (e.touches.length < 2) {
                touchGesture = null;
                return;
            }
            const [first, second] = getTouchPair(e.touches);
            const { midpoint, distance } = getTouchGeometry(first, second);
            if (!touchGesture ||
                touchGesture.identifiers[0] !== first.identifier ||
                touchGesture.identifiers[1] !== second.identifier ||
                (touchGesture.distance === 0 && distance > 0)) {
                beginTouchGesture(e.touches);
                return;
            }
            const zoomFactor = touchGesture.distance > 0 ? distance / touchGesture.distance : 1;
            if (!Number.isFinite(zoomFactor) || zoomFactor <= 0)
                return;
            const camera = zoomAtPoint(touchGesture.camera, touchGesture.midpoint, zoomFactor);
            state.setCamera(Object.assign(Object.assign({}, camera), { panX: camera.panX + midpoint.x - touchGesture.midpoint.x, panY: camera.panY + midpoint.y - touchGesture.midpoint.y }));
            render();
            return;
        }
        if (e.touches.length !== 1)
            return;
        const touch = e.touches[0];
        const mouseEvent = new MouseEvent("mousemove", {
            clientX: touch.clientX,
            clientY: touch.clientY,
        });
        mainCanvas.dispatchEvent(mouseEvent);
    }
    function handleTouchEnd(e) {
        e.preventDefault();
        if (touchNavigating) {
            if (e.touches.length >= 2)
                beginTouchGesture(e.touches);
            else
                touchGesture = null;
            if (e.touches.length === 0)
                touchNavigating = false;
            return;
        }
        stopDrawing();
    }
    saveBtn.addEventListener("click", () => saveImage());
    mainCanvas.addEventListener("contextmenu", (e) => {
        e.preventDefault();
    });
    clearBtn.addEventListener("click", () => {
        if (state.getStrokes().length === 0)
            return;
        clearDialog.returnValue = "";
        clearDialog.showModal();
    });
    clearDialog.addEventListener("close", () => {
        if (clearDialog.returnValue !== "confirm")
            return;
        clearBoard(mainCanvas, mainCtx);
        refreshControls();
    });
    helpBtn.addEventListener("click", () => {
        closeColorPopover();
        closeStrokeSizePopover();
        helpDialog.showModal();
    });
    helpDialog.addEventListener("click", (event) => {
        if (event.target !== helpDialog)
            return;
        const bounds = helpDialog.getBoundingClientRect();
        const clickedOutside = event.clientX < bounds.left ||
            event.clientX > bounds.right ||
            event.clientY < bounds.top ||
            event.clientY > bounds.bottom;
        if (clickedOutside)
            helpDialog.close();
    });
    undoBtn.addEventListener("click", () => {
        undoLastStroke(mainCanvas, mainCtx);
        refreshControls();
    });
    window.addEventListener("resize", () => {
        const color = String(mainCtx.strokeStyle);
        mainCanvas.width = window.innerWidth;
        mainCanvas.height = window.innerHeight;
        alignmentCanvas.width = window.innerWidth;
        alignmentCanvas.height = window.innerHeight;
        configureDrawingContext(color);
        render();
    });
    colorBtn.addEventListener("click", () => {
        const willOpen = colorDropdown.hidden;
        closeStrokeSizePopover();
        colorDropdown.hidden = !willOpen;
        colorBtn.setAttribute("aria-expanded", String(willOpen));
    });
    strokeSizeBtn.addEventListener("click", () => {
        const willOpen = strokeSizeDropdown.hidden;
        closeColorPopover();
        strokeSizeDropdown.hidden = !willOpen;
        strokeSizeBtn.setAttribute("aria-expanded", String(willOpen));
    });
    colorOptions.forEach((option) => {
        option.addEventListener("click", () => {
            const selectedColor = option.dataset.color;
            if (selectedColor) {
                selectColor(selectedColor);
                closeColorPopover();
                colorBtn.focus();
            }
        });
    });
    customColor.addEventListener("input", () => {
        selectColor(customColor.value);
    });
    strokeSizeOptions.forEach((option) => {
        option.addEventListener("click", () => {
            const size = Number(option.dataset.size);
            if (!Number.isFinite(size) || size <= 0)
                return;
            selectStrokeSize(size);
            closeStrokeSizePopover();
            strokeSizeBtn.focus();
        });
    });
    document.addEventListener("pointerdown", (event) => {
        const target = event.target;
        if (target instanceof Node &&
            !colorDropdown.contains(target) &&
            !colorBtn.contains(target)) {
            closeColorPopover();
        }
        if (target instanceof Node &&
            !strokeSizeDropdown.contains(target) &&
            !strokeSizeBtn.contains(target)) {
            closeStrokeSizePopover();
        }
    });
    window.addEventListener("keydown", (event) => {
        if (event.key !== "Escape")
            return;
        if (!colorDropdown.hidden) {
            closeColorPopover();
            colorBtn.focus();
        }
        else if (!strokeSizeDropdown.hidden) {
            closeStrokeSizePopover();
            strokeSizeBtn.focus();
        }
    });
});
